
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GeneralInfoTab } from './GeneralInfoTab';
import { LocationTab } from './LocationTab';
import { LogoTab } from './LogoTab';
import { BusinessFormData } from './types';
import { Business } from '@/data/types/businessTypes';
import { LogoLayout } from '../logo/useLogoUpload';

interface TabsSectionProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  formData: BusinessFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (value: string, field: string) => void;
  date?: Date;
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  handleDateSelect: (date: Date | undefined) => void;
  displayNameManuallyEdited: boolean;
  business: Business;
  logoProps: {
    logoPreview: string | null;
    uploadError: string | null;
    previewTab: string;
    setPreviewTab: (tab: string) => void;
    logoLayout: LogoLayout;
    setLogoLayout: (layout: LogoLayout) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSaveLogo: () => void;
    handleReset: () => void;
    handleRemoveLogo: () => void;
  };
}

export const TabsSection: React.FC<TabsSectionProps> = ({
  activeTab,
  setActiveTab,
  formData,
  handleInputChange,
  handleSelectChange,
  date,
  calendarOpen,
  setCalendarOpen,
  handleDateSelect,
  displayNameManuallyEdited,
  business,
  logoProps
}) => {
  // Make sure activeTab is one of the valid tab values
  const normalizedActiveTab = ['general', 'business-info', 'location', 'logo'].includes(activeTab) 
    ? activeTab 
    : 'business-info';

  // Map 'general' to 'business-info' for backward compatibility
  const mappedActiveTab = normalizedActiveTab === 'general' ? 'business-info' : normalizedActiveTab;

  return (
    <Tabs value={mappedActiveTab} onValueChange={setActiveTab} className="w-full">
      <div className="relative mb-6">
        <ScrollArea className="w-full">
          <TabsList className="inline-flex min-w-full px-2">
            <TabsTrigger value="business-info" className="flex-1 whitespace-nowrap">General Info</TabsTrigger>
            <TabsTrigger value="location" className="flex-1 whitespace-nowrap">Location</TabsTrigger>
            <TabsTrigger value="logo" className="flex-1 whitespace-nowrap">Logo</TabsTrigger>
          </TabsList>
        </ScrollArea>
      </div>
      
      <div className="bg-white rounded-md p-6 shadow-sm">
        <TabsContent value="business-info">
          <GeneralInfoTab 
            formData={formData}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            date={date}
            setDate={() => {}} // This is handled by handleDateSelect
            calendarOpen={calendarOpen}
            setCalendarOpen={setCalendarOpen}
            handleDateSelect={handleDateSelect}
            displayNameManuallyEdited={displayNameManuallyEdited}
          />
        </TabsContent>
        
        <TabsContent value="location">
          <LocationTab 
            formData={formData}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
          />
        </TabsContent>
        
        <TabsContent value="logo">
          <LogoTab 
            business={business}
            logoPreview={logoProps.logoPreview}
            uploadError={logoProps.uploadError}
            previewTab={logoProps.previewTab}
            setPreviewTab={logoProps.setPreviewTab}
            logoLayout={logoProps.logoLayout}
            setLogoLayout={logoProps.setLogoLayout}
            handleFileChange={logoProps.handleFileChange}
            handleSaveLogo={logoProps.handleSaveLogo}
            handleReset={logoProps.handleReset}
            handleRemoveLogo={logoProps.handleRemoveLogo}
          />
        </TabsContent>
      </div>
    </Tabs>
  );
};
